// src/pages/monitoring/progress/ProjectProgressDetail.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, CheckCircle, Clock, XCircle, Package, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import projectProgressService, {
  getRAGStatusBadgeClass,
  getProgressStatusBadgeClass,
  formatProgress,
  formatCurrency
} from '../../../services/projectProgressService';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Project Progress Detail Component
 * 
 * Shows:
 * 1. Project info card (blue header)
 * 2. Large RAG status circle (top right)
 * 3. Summary cards (completed/in progress/not started milestones)
 * 4. Table of segments/sites with progress
 * 5. "View/Update Progress" button per segment/site
 */
const ProjectProgressDetail = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchProjectDetail();
    }
  }, [projectId]);

  const fetchProjectDetail = async () => {
    setIsLoading(true);
    try {
      const data = await projectProgressService.getProjectProgressById(projectId);
      setProject(data);
      console.log('✅ Project detail loaded:', data);
    } catch (error) {
      console.error('❌ Error loading project detail:', error);
      toast.error('Failed to load project details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSegmentProgress = (segmentId) => {
    navigate(`/monitoring/progress/segment/${segmentId}`);
  };

  const handleViewSiteProgress = (siteId) => {
    navigate(`/monitoring/progress/site/${siteId}`);
  };

  if (isLoading || !project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    );
  }

  const items = project.projectType === 'TOWER' ? project.towerSites : project.foSegments;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/monitoring/progress')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">Project Progress Details</h1>
            <p className="text-gray-600 mt-1">{project.projectCode}</p>
          </div>
        </div>
      </div>

      {/* Project Info Card with RAG Status */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-4">Project Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1">Project Code</label>
                  <p className="text-sm font-bold">{project.projectCode}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1">Project Type</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    project.projectType === 'TOWER' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {project.projectType}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProgressStatusBadgeClass(project.progressStatus)}`}>
                    {project.progressStatus?.replace('_', ' ')}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-blue-200 mb-1">Project Name</label>
                  <p className="text-sm font-semibold">{project.projectName}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1">Customer</label>
                  <p className="text-sm font-semibold">{project.customerName}</p>
                </div>
              </div>
            </div>
            
            {/* RAG Status Circle */}
            <div className="ml-8 flex flex-col items-center">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center shadow-xl border-4 border-white ${getRAGStatusBadgeClass(project.overallRAGStatus)}`}>
                <span className="text-white font-bold text-lg">RAG</span>
              </div>
              <div className="mt-3 text-center">
                <div className="text-3xl font-bold">{formatProgress(project.totalProgress)}</div>
                <div className="text-sm text-blue-200">Overall Progress</div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-700">{project.completedMilestones || 0}</div>
                  <div className="text-sm text-green-600">Completed</div>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-700">{project.inProgressMilestones || 0}</div>
                  <div className="text-sm text-yellow-600">In Progress</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-700">
                    {(project.totalMilestones || 0) - (project.completedMilestones || 0) - (project.inProgressMilestones || 0)}
                  </div>
                  <div className="text-sm text-gray-600">Not Started</div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline & Budget Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Timeline</h4>
              <div className="text-sm text-gray-600">
                <p>Days Remaining: <span className={`font-bold ${project.isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                  {project.isOverdue ? 'OVERDUE' : project.daysRemaining}
                </span></p>
                <p className="mt-1">Expected Progress: <span className="font-bold">{formatProgress(project.expectedProgress)}</span></p>
                <p className="mt-1">Variance: <span className={`font-bold ${
                  project.progressVariance >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {project.progressVariance > 0 ? '+' : ''}{(project.progressVariance || 0).toFixed(1)}%
                </span></p>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Budget</h4>
              <div className="text-sm text-gray-600">
                <p>Total Budget: <span className="font-bold text-gray-900">{formatCurrency(project.totalBudget)}</span></p>
                <p className="mt-1">Actual Cost: <span className="font-bold text-gray-900">{formatCurrency(project.actualCost)}</span></p>
                <p className="mt-1">Utilization: <span className="font-bold">{(project.budgetUtilization || 0).toFixed(1)}%</span></p>
              </div>
            </div>
          </div>

          {/* Segments/Sites Table */}
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              {project.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments'}
              <span className="text-sm font-normal text-gray-500">({items?.length || 0} items)</span>
            </h3>

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${project.projectType === 'TOWER' ? 'bg-purple-600' : 'bg-green-600'} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">No</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      {project.projectType === 'TOWER' ? 'Site ID' : 'Span ID'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      {project.projectType === 'TOWER' ? 'Site Name' : 'Segment Name'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">RAG</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items?.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          project.projectType === 'TOWER' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {project.projectType === 'TOWER' ? item.siteId : item.spanId}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {project.projectType === 'TOWER' ? item.siteName : item.siteNameAB}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {project.projectType === 'TOWER' ? item.regional : item.mainArea}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[60px]">
                            <div 
                              className={`rounded-full h-2 transition-all ${
                                project.projectType === 'TOWER' ? 'bg-purple-600' : 'bg-green-600'
                              }`}
                              style={{ width: `${Math.min(item.progressPercentage || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-700 min-w-[40px]">
                            {formatProgress(item.progressPercentage)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`w-8 h-8 rounded-full ${getRAGStatusBadgeClass(item.ragStatus)}`}></div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => project.projectType === 'TOWER' 
                            ? handleViewSiteProgress(item.id) 
                            : handleViewSegmentProgress(item.id)
                          }
                          className={`px-4 py-2 rounded-lg font-medium text-sm text-white shadow-md hover:shadow-lg transition-all ${
                            project.projectType === 'TOWER' 
                              ? 'bg-purple-600 hover:bg-purple-700' 
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          <Eye className="w-4 h-4 inline mr-1" />
                          View Progress
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectProgressDetail;